/**
 * Time stretching: change how long audio lasts without changing its pitch.
 *
 * The method is WSOLA — waveform-similarity overlap-add. Audio is copied out in
 * overlapping windowed frames; the output frames advance by a fixed hop, the
 * input frames advance by that hop divided by the stretch ratio. Overlap-adding
 * windows that do not line up would comb-filter the result, so before each copy
 * the input position is nudged, within a small search radius, to wherever the
 * waveform best matches the natural continuation of the frame already written.
 * Matching the waveform is what keeps the period intact, and keeping the period
 * intact is what keeps the pitch.
 *
 * Why not a phase vocoder: it is the other standard answer and is better on
 * sustained, harmonically rich material, but it smears transients audibly, needs
 * an FFT, and is far harder to hold to a test. WSOLA is arithmetic on samples,
 * so its output length is predictable to the sample and its pitch preservation
 * can be measured directly. Drums keep their attack.
 *
 * Why not resampling: resampling is varispeed — a tape machine. It is already
 * available on every clip (playbackRate with preservePitch off) and is the right
 * choice when the pitch change is the point.
 *
 * This module is pure and synchronous so it can be tested directly. The worker
 * wrapper is in stretch.worker.ts.
 */

/** Ratios closer to 1 than this are not worth the arithmetic, or the artefacts. */
export const STRETCH_EPSILON = 1e-4;

/** Bounds that keep the algorithm in the range where it still sounds like music. */
export const MIN_STRETCH_RATIO = 0.25;
export const MAX_STRETCH_RATIO = 4;

export interface StretchOptions {
  /** Analysis window, in seconds. Longer preserves tone, shorter preserves transients. */
  frameSec?: number;
  /** How far the input position may be nudged to find a better match, in seconds. */
  searchSec?: number;
}

const DEFAULT_FRAME_SEC = 0.04;
const DEFAULT_SEARCH_SEC = 0.005;

/**
 * The correlation is sampled rather than summed over every point. A period long
 * enough to hear is hundreds of samples, so every fourth one locates it just as
 * well for a quarter of the work — and this is the inner loop.
 */
const CORRELATION_STEP = 4;

function hannWindow(length: number): Float32Array {
  const w = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    // Periodic Hann: with 50% overlap, successive windows sum to exactly 1, so
    // the overlap-add needs no normalisation pass.
    w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / length);
  }
  return w;
}

/** Channels summed to mono, used only for alignment. */
function monoOf(channels: readonly Float32Array[]): Float32Array {
  if (channels.length === 1) return channels[0];
  const length = channels[0].length;
  const mono = new Float32Array(length);
  for (const channel of channels) {
    for (let i = 0; i < length; i++) mono[i] += channel[i];
  }
  const scale = 1 / channels.length;
  for (let i = 0; i < length; i++) mono[i] *= scale;
  return mono;
}

/**
 * The offset, within the search radius, that best aligns the input at `center`
 * with the reference window. Normalised cross-correlation, so a quiet passage is
 * matched on its shape rather than losing to a louder misalignment.
 */
function bestOffset(
  mono: Float32Array,
  referenceStart: number,
  center: number,
  search: number,
  length: number,
): number {
  if (search <= 0) return 0;

  let referenceEnergy = 0;
  for (let i = 0; i < length; i += CORRELATION_STEP) {
    const v = mono[referenceStart + i];
    referenceEnergy += v * v;
  }
  // Silence has no shape to match; leave the position where the ratio put it.
  if (referenceEnergy <= 1e-12) return 0;

  let bestScore = -Infinity;
  let best = 0;
  for (let offset = -search; offset <= search; offset++) {
    const start = center + offset;
    if (start < 0 || start + length > mono.length) continue;
    let dot = 0;
    let energy = 0;
    for (let i = 0; i < length; i += CORRELATION_STEP) {
      const a = mono[start + i];
      const b = mono[referenceStart + i];
      dot += a * b;
      energy += a * a;
    }
    const score = dot / Math.sqrt(energy + 1e-12);
    if (score > bestScore) {
      bestScore = score;
      best = offset;
    }
  }
  return best;
}

/** Linear resample, used only for inputs too short for a single analysis frame. */
function resample(channels: readonly Float32Array[], outLength: number): Float32Array[] {
  const inLength = channels[0]?.length ?? 0;
  return channels.map((channel) => {
    const out = new Float32Array(outLength);
    if (inLength === 0) return out;
    for (let i = 0; i < outLength; i++) {
      const x = (i * (inLength - 1)) / Math.max(1, outLength - 1);
      const i0 = Math.floor(x);
      const i1 = Math.min(inLength - 1, i0 + 1);
      const t = x - i0;
      out[i] = channel[i0] * (1 - t) + channel[i1] * t;
    }
    return out;
  });
}

/** Output length in samples for a stretch, so callers can size buffers first. */
export function stretchedLength(inputLength: number, ratio: number): number {
  return Math.max(0, Math.round(inputLength * ratio));
}

/**
 * Stretch `channels` to `ratio` times their length, preserving pitch.
 *
 * A ratio above 1 makes the audio longer and slower; below 1, shorter and
 * faster. Channels are stretched with a shared alignment, so a stereo image
 * survives: choosing offsets per channel independently would move the two sides
 * relative to each other and collapse the width.
 */
export function stretchChannels(
  channels: readonly Float32Array[],
  sampleRate: number,
  ratio: number,
  options: StretchOptions = {},
): Float32Array[] {
  if (channels.length === 0) return [];

  const clamped = Math.min(MAX_STRETCH_RATIO, Math.max(MIN_STRETCH_RATIO, ratio));
  const inputLength = channels[0]?.length ?? 0;
  const outLength = stretchedLength(inputLength, clamped);

  if (Math.abs(clamped - 1) < STRETCH_EPSILON) return channels.map((c) => new Float32Array(c));
  if (outLength === 0) return channels.map(() => new Float32Array(0));

  return stretchChannelsMapped(channels, sampleRate, outLength, (out) => out / clamped, options);
}

/**
 * Stretch with a ratio that varies along the clip.
 *
 * `inputAt` maps an output sample position to the input sample position it
 * should be reading — any monotonically increasing function. A constant ratio is
 * the straight line `out / ratio`; a clip bending through a tempo change is a
 * piecewise-linear one.
 *
 * This exists because rendering the pieces separately and butting them together
 * does not work. Each piece would be overlap-added from its own starting phase,
 * so every join would step the waveform. Over a ramp, which is subdivided into
 * many pieces, those steps accumulate into something that measures as noise even
 * though each piece is individually clean. One pass, with the read position
 * following the map and the similarity search running straight through every
 * boundary, has no joins at all.
 */
export function stretchChannelsMapped(
  channels: readonly Float32Array[],
  sampleRate: number,
  outLength: number,
  inputAt: (outSample: number) => number,
  options: StretchOptions = {},
): Float32Array[] {
  if (channels.length === 0) return [];
  const inputLength = channels[0]?.length ?? 0;
  if (outLength <= 0) return channels.map(() => new Float32Array(0));

  // An analysis frame has to fit inside the input twice over. Below that there
  // is nothing to overlap-add, and a fragment that short has no audible pitch to
  // preserve, so plain resampling is both honest and adequate.
  const wantedFrame = Math.round((options.frameSec ?? DEFAULT_FRAME_SEC) * sampleRate);
  const frame = Math.min(Math.max(128, wantedFrame), Math.floor(inputLength / 2)) & ~1;
  if (frame < 128) return resample(channels, outLength);

  const hopOut = frame >> 1;
  // The search must not be able to walk the read position backwards past the
  // previous frame, or the output would stutter on repeated material. The
  // smallest input hop anywhere along the map is what bounds it.
  const smallestHopIn = Math.max(
    1,
    Math.min(
      ...Array.from({ length: Math.max(1, Math.ceil(outLength / hopOut)) }, (_, k) =>
        Math.max(1, inputAt((k + 1) * hopOut) - inputAt(k * hopOut)),
      ),
    ),
  );
  const search = Math.max(
    0,
    Math.min(
      Math.round((options.searchSec ?? DEFAULT_SEARCH_SEC) * sampleRate),
      Math.floor(smallestHopIn / 2),
    ),
  );
  const correlationLength = Math.min(hopOut, Math.max(CORRELATION_STEP * 8, Math.round(sampleRate * 0.02)));

  const window = hannWindow(frame);
  const mono = monoOf(channels);
  const out = channels.map(() => new Float32Array(outLength));

  let offset = 0;
  let lastOutPos = 0;

  for (let outPos = 0; outPos < outLength; outPos += hopOut) {
    lastOutPos = outPos;
    const inPos = inputAt(outPos);
    const read = Math.min(Math.max(0, Math.round(inPos) + offset), inputLength - frame);
    const copy = Math.min(frame, outLength - outPos);
    for (let c = 0; c < channels.length; c++) {
      const src = channels[c];
      const dst = out[c];
      for (let i = 0; i < copy; i++) dst[outPos + i] += src[read + i] * window[i];
    }

    // What this frame would have played next, had the audio kept running: the
    // following frame is chosen to continue it as smoothly as possible.
    const referenceStart = read + hopOut;
    const nextCenter = Math.round(inputAt(outPos + hopOut));

    offset =
      referenceStart + correlationLength <= inputLength
        ? bestOffset(mono, referenceStart, nextCenter, search, correlationLength)
        : 0;
  }

  // The first and last half-frames are covered by a single window, so the Hann
  // taper leaves them quiet. Dividing the taper back out restores them; without
  // this every stretched clip would fade itself in and out.
  //
  // The tail has to be measured from where the final frame actually began. That
  // frame is usually truncated by the end of the buffer, so its last written
  // sample is not the last sample of the window, and assuming otherwise divides
  // by a far smaller number than the one actually applied — which shows up as a
  // burst of gain in the closing milliseconds.
  const head = Math.min(hopOut, outLength);
  // A floor on the divisor, rather than a cut-off: it leaves a three-millisecond
  // ramp at each end instead of a step, and stops near-zero window values from
  // amplifying whatever noise sits under them.
  const MIN_GAIN = 0.05;
  for (const channel of out) {
    for (let i = 0; i < head; i++) {
      channel[i] /= Math.max(MIN_GAIN, window[i]);
    }
    for (let index = Math.max(head, lastOutPos + hopOut); index < outLength; index++) {
      const w = window[index - lastOutPos];
      if (w === undefined) break;
      channel[index] /= Math.max(MIN_GAIN, w);
    }
  }

  return out;
}
